/**
 * useAnalytics Hook
 *
 * React hook for analytics tracking using PostHog's React integration.
 * Use this in React components. For non-React contexts, use AnalyticsService.
 *
 * Event routing:
 * - Funnel events → Both GA4 and PostHog
 * - Product events → PostHog only
 * - Error events → PostHog only
 */

import { useCallback, useMemo } from 'react'
import { usePostHog } from 'posthog-js/react'
import {
  ANALYTICS_EVENTS,
  PRODUCT_EVENTS,
  ERROR_EVENTS,
  type ElementType,
  type CanvasAction,
  type SaveMethod,
} from '../constants/analytics'
import { trackBeginCheckout } from '../utils/dataLayer'

type EventProps = Record<string, string | number | boolean | undefined>

/**
 * Send event to Google Analytics 4
 */
const sendToGA4 = (eventName: string, props?: EventProps): void => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, props)
  } else if (import.meta.env.MODE === 'development') {
    // eslint-disable-next-line no-console
    console.debug(`[Analytics GA4] ${eventName}`, props)
  }
}

/**
 * Analytics hook for React components
 */
export function useAnalytics() {
  const posthog = usePostHog()

  // ============================================================
  // CORE FUNNEL EVENTS (sent to BOTH GA4 and PostHog)
  // ============================================================

  const trackFunnel = useCallback(
    (eventName: string, props?: EventProps): void => {
      posthog?.capture(eventName, props)
      sendToGA4(eventName, props)
    },
    [posthog]
  )

  const signUp = useCallback(
    (method: string = 'email'): void => {
      trackFunnel(ANALYTICS_EVENTS.SIGN_UP, { method })
    },
    [trackFunnel]
  )

  const logIn = useCallback(
    (method: string = 'email'): void => {
      trackFunnel(ANALYTICS_EVENTS.LOG_IN, { method })
    },
    [trackFunnel]
  )

  const newBoard = useCallback(
    (templateId: string): void => {
      trackFunnel(ANALYTICS_EVENTS.NEW_BOARD, { template_id: templateId })
    },
    [trackFunnel]
  )

  const createComplete = useCallback(
    (boardId: string, elementCount: number): void => {
      trackFunnel(ANALYTICS_EVENTS.CREATE_COMPLETE, {
        board_id: boardId,
        element_count: elementCount,
      })
    },
    [trackFunnel]
  )

  const editBoard = useCallback(
    (boardId: string, elementCount: number): void => {
      trackFunnel(ANALYTICS_EVENTS.EDIT_BOARD, {
        board_id: boardId,
        element_count: elementCount,
      })
    },
    [trackFunnel]
  )

  const elementAdded = useCallback(
    (type: ElementType): void => {
      trackFunnel(ANALYTICS_EVENTS.ELEMENT_ADDED, { type })
    },
    [trackFunnel]
  )

  const chatSent = useCallback(
    (messageLength: number): void => {
      trackFunnel(ANALYTICS_EVENTS.CHAT_SENT, { message_length: messageLength })
    },
    [trackFunnel]
  )

  const canvasAction = useCallback(
    (action: CanvasAction): void => {
      trackFunnel(ANALYTICS_EVENTS.CANVAS_ACTION, { action })
    },
    [trackFunnel]
  )

  const purchaseStarted = useCallback(
    (price: number, size: string): void => {
      trackBeginCheckout({
        value: price,
        size,
      })
      trackFunnel(ANALYTICS_EVENTS.PURCHASE_STARTED, {
        // GA4 ecommerce required fields
        currency: 'USD',
        value: price,
        items: [
          {
            item_id: 'printboard',
            item_name: 'Custom Vision Board – Printed & Shipped',
            item_variant: size,
            price: price,
            quantity: 1,
          },
        ],
      })
    },
    [trackFunnel]
  )

  const purchase = useCallback(
    (transactionId: string, value: number, currency: string = 'USD'): void => {
      trackFunnel(ANALYTICS_EVENTS.PURCHASE, {
        transaction_id: transactionId,
        value,
        currency,
      })
    },
    [trackFunnel]
  )

  // ============================================================
  // PRODUCT EVENTS (sent to PostHog only)
  // ============================================================

  const trackProduct = useCallback(
    (eventName: string, props?: EventProps): void => {
      posthog?.capture(eventName, props)
    },
    [posthog]
  )

  const boardSaved = useCallback(
    (method: SaveMethod): void => {
      trackProduct(PRODUCT_EVENTS.BOARD_SAVED, { method })
    },
    [trackProduct]
  )

  const boardRenamed = useCallback((): void => {
    trackProduct(PRODUCT_EVENTS.BOARD_RENAMED)
  }, [trackProduct])

  const featureUsed = useCallback(
    (featureName: string): void => {
      trackProduct(PRODUCT_EVENTS.FEATURE_USED, { feature_name: featureName })
    },
    [trackProduct]
  )

  const guidesViewed = useCallback(
    (guideId?: string): void => {
      trackProduct(PRODUCT_EVENTS.GUIDES_VIEWED, { guide_id: guideId })
    },
    [trackProduct]
  )

  const modalOpened = useCallback(
    (modalName: string): void => {
      trackProduct(PRODUCT_EVENTS.MODAL_OPENED, { modal_name: modalName })
    },
    [trackProduct]
  )

  const modalClosed = useCallback(
    (modalName: string, action?: string): void => {
      trackProduct(PRODUCT_EVENTS.MODAL_CLOSED, {
        modal_name: modalName,
        action,
      })
    },
    [trackProduct]
  )

  // ============================================================
  // ERROR EVENTS (sent to PostHog only)
  // ============================================================

  const trackError = useCallback(
    (eventName: string, props?: EventProps): void => {
      posthog?.capture(eventName, props)
    },
    [posthog]
  )

  const printValidationFailed = useCallback(
    (reason: string): void => {
      trackError(ERROR_EVENTS.PRINT_VALIDATION_FAILED, { reason })
    },
    [trackError]
  )

  const purchaseError = useCallback(
    (step: string, error: string): void => {
      trackError(ERROR_EVENTS.PURCHASE_ERROR, { step, error })
    },
    [trackError]
  )

  const aiError = useCallback(
    (type: string, message?: string, metadata?: EventProps): void => {
      trackError(ERROR_EVENTS.AI_ERROR, { type, message, ...metadata })
    },
    [trackError]
  )

  const canvasError = useCallback(
    (error: string, component?: string): void => {
      trackError(ERROR_EVENTS.CANVAS_ERROR, { error, component })
    },
    [trackError]
  )

  // Memoize the returned object to prevent infinite loops in components
  // that use analytics in useCallback dependencies
  return useMemo(
    () => ({
      // Core funnel
      trackFunnel,
      signUp,
      logIn,
      newBoard,
      createComplete,
      editBoard,
      elementAdded,
      chatSent,
      canvasAction,
      purchaseStarted,
      purchase,
      // Product intelligence
      trackProduct,
      boardSaved,
      boardRenamed,
      featureUsed,
      guidesViewed,
      modalOpened,
      modalClosed,
      // Error tracking
      trackError,
      printValidationFailed,
      purchaseError,
      aiError,
      canvasError,
    }),
    [
      trackFunnel,
      signUp,
      logIn,
      newBoard,
      createComplete,
      editBoard,
      elementAdded,
      chatSent,
      canvasAction,
      purchaseStarted,
      purchase,
      trackProduct,
      boardSaved,
      boardRenamed,
      featureUsed,
      guidesViewed,
      modalOpened,
      modalClosed,
      trackError,
      printValidationFailed,
      purchaseError,
      aiError,
      canvasError,
    ]
  )
}

export default useAnalytics
